import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import { useTemplates } from "../useTemplates";
import "./TemplateList.scss";

/**
 * Collapsible sidebar listing notification templates with automated/manual toggles (stub API).
 * @param {object} props
 * @param {boolean} props.expanded When false, renders a narrow rail with an expand control.
 * @param {(expanded: boolean) => void} props.onExpandedChange Toggles expanded state from header or rail.
 * @returns {import('react').JSX.Element}
 */
const TemplateList = ({ expanded, onExpandedChange }) => {
    const {
        isLoading,
        isError,
        templateList,
        updateSendTypeMutation,
    } = useTemplates();

    if (!expanded) {
        return (
            <aside
                className="template-list template-list--collapsed"
                aria-label="Notification templates"
            >
                <button
                    type="button"
                    className="template-list__collapsed-rail"
                    onClick={() => onExpandedChange(true)}
                    aria-label="Expand template list"
                    aria-expanded="false"
                >
                    <span
                        className="template-list__collapsed-chevron"
                        aria-hidden="true"
                    >
                        <IoChevronForward size="1.25rem" />
                    </span>
                    <span className="template-list__collapsed-label-wrap">
                        <span className="template-list__collapsed-label">
                            TEMPLATES
                        </span>
                    </span>
                </button>
            </aside>
        );
    }

    return (
        <aside
            className="template-list template-list--layout"
            aria-label="Notification templates"
        >
            <div className="template-list__header">
                <span>Template</span>
                <span>Auto</span>
                <button
                    type="button"
                    className="template-list__toggle"
                    onClick={() => onExpandedChange(false)}
                    aria-label="Collapse template list"
                    aria-expanded="true"
                >
                    <IoChevronBack size="1.25rem" />
                </button>
            </div>
            <div className="template-list__body">
                {isLoading && (
                    <div
                        className="template-list__status"
                        role="status"
                        aria-live="polite"
                    >
                        <span className="loading-with-label">
                            Loading templates
                            <span
                                className="loader loader--inline"
                                aria-hidden="true"
                            />
                        </span>
                    </div>
                )}
                {isError && (
                    <div className="template-list__status template-list__status--error">
                        Error loading templates.
                    </div>
                )}
                {!isLoading && !isError && templateList.length === 0 && (
                    <div className="template-list__status">
                        No templates returned.
                    </div>
                )}
                {!isLoading && !isError && templateList.length > 0 && (
                    <ul className="template-list__list" role="list">
                        {templateList.map((row) => (
                            <li
                                key={row.template}
                                className="template-list__row"
                            >
                                <span
                                    className="template-list__name"
                                    title={row.template}
                                >
                                    {row.template}
                                </span>
                                <ToggleSwitch
                                    checked={row.sendType === "automated"}
                                    onToggle={(nextAutomated) => {
                                        const sendType = nextAutomated
                                            ? "automated"
                                            : "manual";
                                        updateSendTypeMutation.mutate({
                                            template: row.template,
                                            sendType,
                                        });
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </aside>
    );
};

export default TemplateList;
